import React, { useCallback, useEffect } from 'react';

import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import media, { generateMedia } from 'styled-media-query';

import { ChapterTitle } from './ChapterTitle';
import { ProgramElementBackArchive } from '../ProgramElementBackArchive';
import { ProgramElementPagination } from '../ProgramElementPagination';
import { ChapterStudyLog } from './ChapterStudyLog';
import { ChapterExamLink } from './ChapterExamLink';
import { ProgramElementsNav } from '../ProgramElementsNav';

import { CLASS_NAME_MARKDOWN_BODY, Markdown } from '../../../../../common/Markdown';

import {
  ProgramDetailFragment,
  StudyLogFragment,
  ChapterFragment,
  ExamQuestionFragment,
} from '../../../../../gen/graphql';
import { scrollToID } from '../../../../../utils/ui/Anchor';
import { getChaptersAndPractices } from '../programUtil';

interface Props {
  programID: number;
  chapterID: number;
  program: ProgramDetailFragment | null | undefined;
  chapter: ChapterFragment | null | undefined;
  programExam: ExamQuestionFragment[] | null | undefined;
  studyLog: StudyLogFragment | null | undefined;
  refetchStudyLog: (() => Promise<unknown>) | null | undefined;
  openCompleteModal: () => void;
  hasPermissionToReadStudyLog: boolean;
  hasPermissionToReadExam: boolean;
  hasPermissionToReadExamForInstructorAndCoach: boolean;
  isLoggedIn: boolean;
  personalInfoVisible: boolean;
  programElementsVisible: boolean;
}

const CLASS_NAME_TUTORIAL = 'tutorial';

export const ChapterDetailContent = (props: Props): React.ReactElement => {
  const location = useLocation();

  const examScores = props.chapter?.examScores ?? [];
  const maxScore =
    examScores.length <= 0 ? undefined : examScores.reduce((acc, cur) => (acc.score > cur.score ? acc : cur));

  const programData = props.program ?? undefined;
  const programElements = getChaptersAndPractices(programData);
  const currentChapterIndexInProgramElements = programElements.findIndex(
    ({ __typename: typename, id }) => typename === 'Chapter' && id === props.chapterID,
  );
  const chapterElements = programElements.filter((element) => element.__typename === 'Chapter');
  const currentChapterIndexInChapterElements = chapterElements.findIndex(({ id }) => id === props.chapterID);
  const isLastChapter = currentChapterIndexInChapterElements === chapterElements.length - 1;
  const isOnlyChapter = chapterElements.length === 1;

  const scrollPage = useCallback((): void => {
    const anchor = location.hash;
    if (anchor) {
      try {
        const hash = decodeURI(anchor.replace(/^#/, ''));
        if (hash) {
          scrollToID(hash);
        }
      } catch (e) {
        if (e instanceof URIError) {
          window.scrollTo(0, 0);
        }
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollPage();
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [scrollPage]);

  return (
    <>
      <TutorialWrapper>
        <div className={CLASS_NAME_TUTORIAL}>
          <Content data-e2e="chapterContent">
            <StyledChapterTitle
              title={props.chapter ? `${props.chapter.number}章 ${props.chapter.title}` : ''}
              description={props.chapter?.description}
              requireTime={props.chapter?.requireTime}
              examScore={maxScore}
              studyLog={props.chapter?.studyLog?.understandingLevel}
              personalInfoVisible={props.personalInfoVisible}
            />
            <MarkdownWrapper>
              <Markdown key={props.chapterID} content={props.chapter?.content} type="program" />
            </MarkdownWrapper>
          </Content>
          {props.hasPermissionToReadStudyLog && props.refetchStudyLog && (
            <ChapterStudyLog
              chapterID={props.chapterID}
              studyLog={props.studyLog ?? null}
              onCheckStudyLogStatusChanged={() => props.refetchStudyLog!().then()}
              modalOpen={props.openCompleteModal}
              isLastChapter={isLastChapter}
              isOnlyChapter={isOnlyChapter}
            />
          )}
          {Boolean(props.programExam?.length) && (
            <>
              {props.hasPermissionToReadExam && (
                <ChapterExamLink
                  display={Boolean(props.studyLog)}
                  programId={props.programID}
                  chapterId={props.chapterID}
                  examScores={props.chapter?.examScores}
                />
              )}
              {props.hasPermissionToReadExamForInstructorAndCoach && (
                <ExamLinkForInstructor>
                  <Link to={`/programs/${props.programID}/chapters/${props.chapterID}/exam`}>テストを確認</Link>
                </ExamLinkForInstructor>
              )}
            </>
          )}
          {programData && (
            <ProgramElementPagination
              program={programData}
              currentChapterIndex={currentChapterIndexInProgramElements}
              disabledNext={props.hasPermissionToReadStudyLog && !props.studyLog}
            />
          )}
          {props.programElementsVisible && (
            <ProgramElementBackArchive
              programID={props.programID}
              chapterID={props.chapterID}
              isLogin={props.isLoggedIn}
            />
          )}
        </div>
        {props.programElementsVisible && (
          <ProgramElementsNav
            programElements={programElements}
            currentID={props.chapterID}
            currentChapterContent={props.chapter?.content}
            programID={props.programID}
          />
        )}
      </TutorialWrapper>
    </>
  );
};

const customMedia = generateMedia({
  tablet: '1024px',
});

const TutorialWrapper = styled.div`
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0 auto;

  ${media.lessThan('medium')`
    padding-bottom: 3rem;
  `}

  .${CLASS_NAME_TUTORIAL} {
    flex: 1;
    min-width: 0;

    .${CLASS_NAME_MARKDOWN_BODY} {
      scroll-snap-type: y mandatory;

      h2 {
        scroll-snap-align: start;
        scroll-margin-top: 1em;

        &:first-child {
          scroll-snap-align: start;
          scroll-margin-bottom: -1em;
        }
      }
    }
  }

  ${media.lessThan('large')`
    .${CLASS_NAME_TUTORIAL} {
      width: calc(100% - 15rem);
    }
  `}

  ${customMedia.lessThan('tablet')`
    .${CLASS_NAME_TUTORIAL} {
      width: 100%;
    }
  `}
`;

const Content = styled.div`
  position: relative;

  svg {
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
  }
`;

const StyledChapterTitle = styled(ChapterTitle)`
  ${media.lessThan('medium')`
    margin: -2rem -1rem 0;
  `}
`;

const MarkdownWrapper = styled.div`
  margin: 0 auto;
  padding: 2rem;
  overflow: hidden;

  ${media.lessThan('medium')`
    padding: 1.5rem 0;
  `}
`;

const ExamLinkForInstructor = styled.p`
  margin: 2rem auto;
  text-align: center;

  a {
    color: #eb0000;
    font-size: 1rem;
    font-weight: bold;
  }
`;
